/**
 * VoidDiscountPopup - Multi-step discount popup for Shopify theme
 *
 * Features:
 * - 3-step flow: Question -> Email -> Success
 * - localStorage persistence (7-day cooldown)
 * - Theme Editor support
 * - Smooth transitions
 * - Body scroll lock
 */

class VoidDiscountPopup {
  #element;
  #currentStep = 1;
  #delayMs;
  #storageKey = 'void_popup_dismissed';
  #cooldownDays = 7;
  #steps;
  #closeButtons;
  #optionButtons;
  #emailForm;
  #copyButton;
  #selectedProfile = null;
  #controller = new AbortController();

  constructor(element) {
    this.#element = element;
    this.#delayMs = (parseInt(element.dataset.delay, 10) || 5) * 1000;

    this.#steps = {
      1: element.querySelector('[data-step="1"]'),
      2: element.querySelector('[data-step="2"]'),
      3: element.querySelector('[data-step="3"]'),
    };

    this.#closeButtons = element.querySelectorAll('[data-popup-close]');
    this.#optionButtons = element.querySelectorAll('[data-option]');
    this.#emailForm = element.querySelector('form');
    this.#copyButton = element.querySelector('[data-copy-code]');

    this.#init();
  }

  #init() {
    if (!this.#isThemeEditor() && this.#wasRecentlyDismissed()) {
      return;
    }

    this.#bindEvents();
    this.#schedulePopup();
  }

  #bindEvents() {
    const { signal } = this.#controller;

    for (const btn of this.#closeButtons) {
      btn.addEventListener('click', () => this.#close(), { signal });
    }

    this.#element.addEventListener('click', (e) => {
      if (e.target === this.#element) this.#close();
    }, { signal });

    for (const btn of this.#optionButtons) {
      btn.addEventListener('click', () => this.#handleOptionClick(btn), { signal });
    }

    this.#emailForm?.addEventListener('submit', (e) => this.#handleEmailSubmit(e), { signal });

    this.#copyButton?.addEventListener('click', () => this.#copyDiscountCode(), { signal });
  }

  #schedulePopup() {
    setTimeout(() => this.#show(), this.#delayMs);
  }

  #show() {
    this.#element.classList.add('void-popup--visible');
    document.body.style.overflow = 'hidden';
    this.#showStep(1);
  }

  #close() {
    this.#element.classList.remove('void-popup--visible');
    document.body.style.overflow = '';
    this.#markDismissed();
  }

  #showStep(stepNumber) {
    this.#currentStep = stepNumber;

    for (const step of Object.values(this.#steps)) {
      if (step) step.style.display = 'none';
    }

    this.#steps[stepNumber]?.setAttribute('style', 'display: block');
  }

  #handleOptionClick(button) {
    const selectedOption = button.dataset.option;

    for (const btn of this.#optionButtons) {
      btn.classList.remove('selected');
    }
    button.classList.add('selected');

    this.#selectedProfile = selectedOption;

    setTimeout(() => this.#showStep(2), 300);
  }

  #handleEmailSubmit(e) {
    e.preventDefault();

    const emailInput = this.#emailForm?.querySelector('input[type="email"]');
    const email = emailInput?.value?.trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    this.#showStep(3);

    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'void_popup_conversion',
        profile: this.#selectedProfile,
      });
    }
  }

  #copyDiscountCode() {
    const codeElement = this.#element.querySelector('[data-discount-code]');
    const code = codeElement?.textContent?.trim() ?? codeElement?.value?.trim();

    if (!code) return;

    const showFeedback = () => {
      const originalText = this.#copyButton.textContent;
      this.#copyButton.textContent = 'Copié !';
      setTimeout(() => {
        this.#copyButton.textContent = originalText;
      }, 2000);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(showFeedback).catch(() => {
        this.#fallbackCopy(code) && showFeedback();
      });
    } else {
      this.#fallbackCopy(code) && showFeedback();
    }
  }

  #fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;opacity:0;left:-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    let success = false;
    try {
      success = document.execCommand('copy');
    } catch {
      // Ignore
    }
    textarea.remove();
    return success;
  }

  #isThemeEditor() {
    return typeof Shopify !== 'undefined' && Shopify.designMode === true;
  }

  #wasRecentlyDismissed() {
    const dismissed = localStorage.getItem(this.#storageKey);
    if (!dismissed) return false;

    const dismissedTime = parseInt(dismissed, 10);
    if (Number.isNaN(dismissedTime)) return false;

    const cooldownMs = this.#cooldownDays * 24 * 60 * 60 * 1000;
    return (Date.now() - dismissedTime) < cooldownMs;
  }

  #markDismissed() {
    if (this.#isThemeEditor()) return;
    localStorage.setItem(this.#storageKey, Date.now().toString());
  }

  destroy() {
    this.#controller.abort();
  }
}

// Auto-initialization
const initVoidPopup = () => {
  const popupElement = document.querySelector('[data-void-discount-popup]');

  if (popupElement && !popupElement._voidPopupInstance) {
    popupElement._voidPopupInstance = new VoidDiscountPopup(popupElement);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVoidPopup);
} else {
  initVoidPopup();
}

document.addEventListener('shopify:section:load', (e) => {
  const popupElement = e.target?.querySelector('[data-void-discount-popup]');

  if (popupElement) {
    popupElement._voidPopupInstance?.destroy();
    delete popupElement._voidPopupInstance;
    popupElement._voidPopupInstance = new VoidDiscountPopup(popupElement);
  }
});
